import { visuallyHidden } from "@mui/utils";
import TableHead from "@mui/material/TableHead";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Box from "@mui/material/Box";
import {} from "../../ExamOverview/ExamOverview";
import { ExamOverviewRow } from "../../../interfaces/examInterfaces";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
  const aRaw = a[orderBy];
  const bRaw = b[orderBy];

  // Check for dot-separated number strings
  const isDotNumberString = (val: any): val is string => typeof val === "string" && /^\d+(\.\d+)*\b/.test(val);

  if (isDotNumberString(aRaw) && isDotNumberString(bRaw)) {
    const aVersion = extractVersionPrefix(aRaw);
    const bVersion = extractVersionPrefix(bRaw);

    if (aVersion && bVersion) {
      const aParts = aVersion.split(".").map(Number);
      const bParts = bVersion.split(".").map(Number);
      const maxLength = Math.max(aParts.length, bParts.length);

      for (let i = 0; i < maxLength; i++) {
        const aNum = aParts[i] ?? 0;
        const bNum = bParts[i] ?? 0;
        if (aNum < bNum) return 1;
        if (aNum > bNum) return -1;
      }
      return 0;
    }
  }

  // Fallback using normalizeValue
  const aVal = normalizeValue(aRaw);
  const bVal = normalizeValue(bRaw);

  if (typeof aVal === "string" && typeof bVal === "string") {
    const aLower = aVal.toLowerCase();
    const bLower = bVal.toLowerCase();

    if (aLower < bLower) return 1;
    if (aLower > bLower) return -1;
    return 0;
  }

  if (aVal < bVal) return 1;
  if (aVal > bVal) return -1;
  return 0;
}

function extractVersionPrefix(value: string): string | null {
  const match = value.match(/^\d+(?:\.\d+)*\b/);
  return match ? match[0] : null;
}

function normalizeValue(value: unknown): number | string {
  if (value === undefined) {
    return 0;
  }
  if (typeof value === "boolean") {
    return value ? 1 : 0;
  }
  return value as number | string;
}

export type Order = "asc" | "desc";

export function getComparator<Key extends keyof ExamOverviewRow>(
  order: Order,
  orderBy: Key
): (a: ExamOverviewRow, b: ExamOverviewRow) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof ExamOverviewRow;
  label: string;
  numeric: boolean;
}

export const headCells: readonly HeadCell[] = [
  {
    id: "isDirectory",
    numeric: false,
    disablePadding: true,
    label: "",
  },
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "Name",
  },
  {
    id: "grade",
    numeric: false,
    disablePadding: false,
    label: "Klasse",
  },
];

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof ExamOverviewRow) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

export default function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property: keyof ExamOverviewRow) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell key="showOptions" align="left" padding="normal"></TableCell>
      </TableRow>
    </TableHead>
  );
}
